<template>
  <div v-show="isVisible" class="c-easy-toast">
    <ProgressBar
      class="c-easy-toast__progress-bar"
      :value="progressBarValue"
      :show-value="false"
    />
    <div class="c-easy-toast__in">
      <div class="c-easy-toast__text">
        {{ text }}
      </div>
    </div>
    <button type="button" @click="hide">OK</button>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'
import { useEventBus, useIsMobile } from '../EasyToastPlugin'
import AWN from 'awesome-notifications'
import ProgressBar from 'primevue/progressbar'

type Toast = {
  success(message: string)
  warning(message: string)
}

export default defineComponent({
  components: {
    ProgressBar,
  },
  setup() {
    const duration = 5000
    const desktopToast = new AWN({
      position: 'bottom-right',
      labels: { success: 'SUKCES' },
    }) as Toast

    let timeout
    let timer
    const eventBus = useEventBus()
    const text = ref('')
    const isVisible = ref(false)
    const isMobile = useIsMobile()
    const progressBarValue = ref(0)

    const startProgressBar = () => {
      const interval = 50
      const step = 100 / (duration / interval)
      timer = setInterval(() => (progressBarValue.value += step), interval)
    }

    const show = (msg) => {
      window.clearTimeout(timeout)

      progressBarValue.value = 0
      text.value = msg
      isVisible.value = true

      startProgressBar()
      timeout = setTimeout(() => hide(), duration)
    }

    const hide = () => {
      text.value = ''
      isVisible.value = false
      window.clearTimeout(timeout)
      window.clearInterval(timer)
    }

    eventBus.on('notifySuccess', (msg) => {
      if (isMobile.value) {
        show(msg)
      } else {
        desktopToast.success(msg)
      }
    })

    eventBus.on('notifyWarning', (msg) => {
      if (isMobile.value) {
        show(msg)
      } else {
        desktopToast.warning(msg)
      }
    })

    return {
      text,
      isVisible,
      hide,
      progressBarValue,
    }
  },
})
</script>

<style lang="scss">
.c-easy-toast {
  .p-progressbar .p-progressbar-value {
    background: #40871d;
    transition-duration: 0ms;
  }
}
</style>

<style lang="scss" scoped>
.c-easy-toast {
  visibility: inherit;
  font-weight: var(--bs-body-font-weight);
  text-align: var(--bs-body-text-align);
  -webkit-text-size-adjust: 100%;
  -webkit-tap-highlight-color: transparent;
  font-family: 'Inter', sans-serif;
  letter-spacing: -0.015rem;
  -webkit-font-smoothing: antialiased;
  box-sizing: border-box;
  --bs-bg-opacity: 1;
  background: #a7d590 !important;
  position: fixed;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 9999;
  min-height: 52px;
  margin-top: env(safe-area-inset-top);
  transition: 0.2s all;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
  padding: 10px 16px;
  line-height: 1.4em;
  display: flex;
  align-items: center;
  justify-content: space-between;
  bottom: 0;
  top: auto;
  margin-bottom: env(safe-area-inset-bottom);

  &__progress-bar {
    position: absolute;
    width: 100%;
    height: 3px;
    bottom: 52px;
    left: 0;
  }

  &__content {
    display: flex;
  }

  &__in {
    visibility: inherit;
    font-weight: var(--bs-body-font-weight);
    text-align: var(--bs-body-text-align);
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: transparent;
    font-family: 'Inter', sans-serif;
    letter-spacing: -0.015rem;
    -webkit-font-smoothing: antialiased;
    --bs-bg-opacity: 1;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.75);
    line-height: 1.4em;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding-right: 20px;
  }

  &__text {
    visibility: inherit;
    font-weight: var(--bs-body-font-weight);
    text-align: var(--bs-body-text-align);
    -webkit-text-size-adjust: 100%;
    letter-spacing: -0.015rem;
    -webkit-font-smoothing: antialiased;
    --bs-bg-opacity: 1;
    line-height: 1.4em;
    width: 100%;
    font-family: 'Roboto';
    cursor: pointer;
    font-size: 14px;
    color: #40871d;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    word-break: break-word;
    box-sizing: border-box;
  }

  button {
    visibility: inherit;
    box-sizing: border-box;
    margin: 0;
    font-family: inherit;
    text-transform: none;
    outline: 0 !important;
    -webkit-appearance: button;
    text-align: center;
    vertical-align: middle;
    user-select: none;
    border: 1px solid transparent;
    line-height: 1.2em;
    box-shadow: none !important;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: none;
    text-decoration: none !important;
    border-radius: 6px;
    border-width: 2px;
    height: 30px;
    padding: 0px 12px;
    background: transparent;
    color: #40871d;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
  }
}
</style>
